import React from 'react';
import { getEntityTypeDisplay, pluralizeEntityType } from '../../../../../utils/entityDisplayUtils';
import TooltipWrapper from '../../../../../shared/atoms/TooltipWrapper';
import { WorkItemBreakdownTooltip } from './WorkItemBreakdownTooltip';

interface EntityCountDisplayProps {
    count: number;
    childEntityTypes?: Record<string, number> | null;
    entityType?: string; // Parent entity type (e.g. 'epic', 'feature')
    type?: string; // Item type (e.g. 'roadmap', 'project')
    className?: string;
    textClassName?: string; // Custom text color/styling class
    showSeparator?: boolean; // Show dot separator before
    showIcon?: boolean; // Show parent entity icon
    textSize?: string; // Custom text size (e.g. '9px', '10px')
}

/**
 * Displays an entity count (e.g. \"5 Work Items\") with a rich breakdown tooltip.
 */
export const EntityCountDisplay: React.FC<EntityCountDisplayProps> = ({ 
    count, 
    childEntityTypes, 
    entityType,
    type,
    className = "",
    textClassName,
    showSeparator = false,
    showIcon = false,
    textSize = '9px'
}) => {
    if (count === 0) return null;

    const { sublabel, icon: ParentIcon } = getEntityTypeDisplay(entityType, type);

    // Use the common WorkItemBreakdownTooltip component
    const tooltipContent = <WorkItemBreakdownTooltip childEntityTypes={childEntityTypes} />;

    return (
        <span className={`inline-flex items-center ${className}`} onClick={(e) => e.stopPropagation()}>
            {showSeparator && (
                 <span className="text-light-text-tertiary dark:text-gray-500 mx-1" style={{ lineHeight: 1, verticalAlign: 'middle' }}>•</span>
            )}
            <TooltipWrapper message={tooltipContent}>
                <span className="inline-flex items-center gap-1.5 cursor-help group py-0.5">
                    <span 
                        className={textClassName || "whitespace-nowrap text-gray-500 dark:text-slate-400 font-medium underline decoration-dotted underline-offset-4 decoration-gray-400/50"}
                        style={{ fontSize: textSize }}
                    >
                        {count} {pluralizeEntityType(count, sublabel)}
                    </span>
                    {showIcon && ParentIcon && (
                        <ParentIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors" />
                    )}
                </span>
            </TooltipWrapper>
        </span>
    );
};
